/**
 * AOIIntelBrief — Fused multi-source intelligence picture for a drawn AOI.
 *
 * Collects in parallel:
 *   Maritime  — live AIS vessels with lifecycle flags (loitering, STS, dark)
 *   Air       — OpenSky flights overhead with pattern flags (ISR, low-slow)
 *   Satellite — Sentinel-2 YOLO detections + dark vessel cross-reference
 *   History   — Entity ontology observations (last 30 days)
 *
 * Generates an AI intelligence narrative via Grok-3.
 */
import { useState } from 'react'
import {
  Satellite, Anchor, Plane, AlertTriangle, FileText, Loader2,
  Shield, Activity, MapPin, Users, ExternalLink, TrendingUp,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Props {
  aoi: Record<string, unknown> | null
  onBriefCollected?: (brief: any) => void
}

function getToken() { return localStorage.getItem('auth_token') || '' }

function fmt(d: string) {
  try { return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) }
  catch { return d?.slice(0, 16) || '—' }
}

function fmtDate(d: string) {
  try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
  catch { return d?.slice(0, 10) || '—' }
}

function Badge({ text, color }: { text: string; color: string }) {
  return <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${color}`}>{text}</span>
}

function SummaryCard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: number; sub?: string; color: string }) {
  return (
    <div className={`flex-1 p-3 rounded-lg border ${color} bg-opacity-10 text-center`}>
      <div className="flex justify-center mb-1">{icon}</div>
      <div className="text-xl font-bold text-slate-100">{value}</div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  )
}

export default function AOIIntelBrief({ aoi, onBriefCollected }: Props) {
  const [brief, setBrief] = useState<any>(null)
  const [report, setReport] = useState<string | null>(null)
  const [collecting, setCollecting] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [includeSat, setIncludeSat] = useState(true)
  const [activeSection, setActiveSection] = useState<'overview' | 'maritime' | 'air' | 'satellite' | 'history' | 'scenario' | 'report'>('overview')

  const headers = {
    'Content-Type': 'application/json',
    ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
  }

  async function collect() {
    if (!aoi) return
    setCollecting(true)
    setError(null)
    setBrief(null)
    setReport(null)
    try {
      const res = await fetch('/api/intel/aoi/brief', {
        method: 'POST',
        headers,
        body: JSON.stringify({ aoi, include_satellite: includeSat }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Collection failed')
      setBrief(data)
      onBriefCollected?.(data)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setCollecting(false)
    }
  }

  async function generateReport() {
    if (!brief) return
    setGenerating(true)
    try {
      const res = await fetch('/api/intel/aoi/report', {
        method: 'POST',
        headers,
        body: JSON.stringify({ brief }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.detail || 'Report failed')
      setReport(data.report)
      setActiveSection('report')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setGenerating(false)
    }
  }

  if (!aoi) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center text-slate-500 gap-3">
        <Shield className="w-12 h-12 opacity-30" />
        <p className="text-sm">Draw or select an AOI on the map to begin intelligence collection</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Control bar */}
      <div className="p-4 border-b border-slate-700/50 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={includeSat} onChange={e => setIncludeSat(e.target.checked)}
                className="rounded border-slate-600" />
              Include Sentinel-2 scan (slower)
            </label>
          </div>
          <Button size="sm" onClick={collect} disabled={collecting}
            className="bg-green-600 hover:bg-green-500 text-white text-xs shrink-0">
            {collecting
              ? <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Collecting...</>
              : <><Activity className="w-3 h-3 mr-1.5" />Collect Intelligence</>}
          </Button>
        </div>

        {collecting && (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <div className="flex gap-1">
              {['AIS', 'OpenSky', 'Sentinel-2', 'Entities', 'OSM', 'GDELT'].map(s => (
                <span key={s} className="px-1.5 py-0.5 bg-slate-700/50 rounded animate-pulse">{s}</span>
              ))}
            </div>
            <span>collecting in parallel...</span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-2 bg-red-900/30 border border-red-700/50 rounded text-xs text-red-300">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            {error}
          </div>
        )}
      </div>

      {brief && (
        <>
          {/* Summary strip */}
          <div className="p-3 border-b border-slate-700/50 space-y-2">
            <div className="flex gap-2">
              <SummaryCard icon={<Anchor className="w-4 h-4 text-blue-400" />} label="Vessels"
                value={brief.summary.vessel_count} color="border-blue-700" />
              <SummaryCard icon={<Plane className="w-4 h-4 text-sky-400" />} label="Aircraft"
                value={brief.summary.aircraft_count} color="border-sky-700" />
              <SummaryCard icon={<Satellite className="w-4 h-4 text-green-400" />} label="Detections"
                value={brief.summary.satellite_detections} color="border-green-700" />
              <SummaryCard icon={<AlertTriangle className="w-4 h-4 text-red-400" />} label="Dark"
                value={brief.summary.dark_vessel_count} sub="vessels" color="border-red-700" />
            </div>

            {brief.anomalies?.length > 0 && (
              <div className="space-y-1">
                {brief.anomalies.map((a: any, i: number) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-red-900/20 border border-red-800/40 rounded text-xs text-red-300">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span><span className="font-bold">[{a.type}]</span> {a.detail}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section tabs */}
          {(() => {
            const satAircraftCount = (brief.satellite?.detections || [])
              .filter((d: any) => (d.class_name || '').toLowerCase().includes('aircraft')).length;
            const airLabel = brief.air.count > 0
              ? `Air (${brief.air.count} live)`
              : satAircraftCount > 0
                ? `Air (0 live / ${satAircraftCount} sat)`
                : 'Air (0)';
            return (
          <div className="flex gap-1 px-3 pt-2 border-b border-slate-700/50 overflow-x-auto">
            {([
              ['overview', 'Overview'],
              ['maritime', `Maritime (${brief.maritime.count})`],
              ['air', airLabel],
              ['satellite', `Satellite (${brief.summary.satellite_detections})`],
              ['history', `History (${brief.entity_history.count})`],
              ['scenario', `Ground (${(brief.scenario?.ground_context?.length || 0)})`],
              ['report', 'AI Report'],
            ] as const).map(([key, label]) => (
              <button key={key} onClick={() => setActiveSection(key as any)}
                className={`shrink-0 px-3 py-1.5 rounded-t text-xs font-medium transition-colors ${
                  activeSection === key
                    ? 'bg-slate-700 text-slate-100 border-b-2 border-green-400'
                    : 'text-slate-500 hover:text-slate-300'
                }`}>
                {label}
              </button>
            ))}
          </div>
            );
          })()}

          {/* Section content */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">

            {/* Overview */}
            {activeSection === 'overview' && (
              <div className="space-y-3">
                <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50 text-xs space-y-1">
                  <div className="text-slate-300 font-medium mb-2">Collection Metadata</div>
                  <div className="grid grid-cols-2 gap-1 text-slate-400">
                    <span>Collected: <span className="text-slate-200">{fmt(brief.collected_at)}</span></span>
                    <span>Center: <span className="text-cyan-400 font-mono">{brief.aoi.center_lat}°N, {brief.aoi.center_lon}°E</span></span>
                    <span>Radius: <span className="text-slate-200">{brief.aoi.radius_km} km</span></span>
                    <span>Anomalies: <span className="text-red-400">{brief.anomalies?.length || 0}</span></span>
                  </div>
                </div>

                {brief.dark_vessel_count > 0 && (
                  <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Anchor className="w-4 h-4 text-red-400" />
                      <span className="text-sm font-bold text-red-300">{brief.dark_vessel_count} Dark Vessel{brief.dark_vessel_count > 1 ? 's' : ''}</span>
                      <Badge text="PRIORITY" color="bg-red-800 text-red-200" />
                    </div>
                    <p className="text-xs text-slate-400 mb-2">Visible on satellite imagery but not broadcasting AIS. Indicators of sanctions evasion, illegal fishing, or covert military activity.</p>
                    {brief.dark_vessels.slice(0, 3).map((dv: any, i: number) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded mb-1 text-xs">
                        {dv.crop_b64 && (
                          <img src={`data:image/jpeg;base64,${dv.crop_b64}`} alt="Dark vessel crop"
                            className="w-14 h-14 object-cover rounded border border-red-800/50" />
                        )}
                        <div className="text-slate-300 space-y-0.5">
                          <div>Position: <span className="text-cyan-400 font-mono">{dv.lat?.toFixed(4)}°, {dv.lon?.toFixed(4)}°</span></div>
                          <div>Confidence: {((dv.confidence || 0) * 100).toFixed(0)}%</div>
                          {dv.nearest_ais_dist_m && <div className="text-slate-500">Nearest AIS: {dv.nearest_ais_dist_m}m</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <Button onClick={generateReport} disabled={generating} className="w-full bg-purple-700 hover:bg-purple-600 text-white text-sm">
                  {generating
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating AI Assessment...</>
                    : <><FileText className="w-4 h-4 mr-2" />Generate AI Intelligence Report</>}
                </Button>
              </div>
            )}

            {/* Maritime */}
            {activeSection === 'maritime' && (
              <div className="space-y-2">
                {brief.maritime.vessels.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-6">
                    {brief.maritime.ais_key_configured === false
                      ? 'AIS collection unavailable — AISSTREAM_API_KEY not configured.'
                      : brief.maritime.collection_error
                        ? `AIS collection error: ${brief.maritime.collection_error}`
                        : 'No AIS-broadcasting vessels detected in this area during the collection window.'}
                  </p>
                ) : (
                  <>
                    <div className="flex gap-2 flex-wrap text-xs text-slate-400">
                      {[
                        { label: 'Total', val: brief.maritime.count, color: 'text-blue-400' },
                        { label: 'Loitering', val: brief.maritime.loitering_count, color: 'text-yellow-400' },
                        { label: 'STS candidates', val: brief.maritime.sts_candidate_count, color: 'text-orange-400' },
                        { label: 'Dark', val: brief.dark_vessel_count, color: 'text-red-400' },
                      ].map(({ label, val, color }) => (
                        <span key={label}>{label}: <span className={`font-bold ${color}`}>{val}</span></span>
                      ))}
                    </div>
                    <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                      {brief.maritime.vessels.map((v: any, i: number) => (
                        <div key={i} className={`p-2.5 rounded-lg border text-xs ${
                          v.dark ? 'border-red-700/50 bg-red-900/10' :
                          v.loitering ? 'border-yellow-700/50 bg-yellow-900/10' :
                          'border-slate-700/50 bg-slate-800/40'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-slate-200">{v.name || 'Unknown Vessel'}</span>
                            <div className="flex gap-1">
                              {v.dark && <Badge text="DARK" color="bg-red-800 text-red-200" />}
                              {v.loitering && <Badge text="LOITERING" color="bg-yellow-800 text-yellow-200" />}
                              {v.sts_candidate && <Badge text="STS" color="bg-orange-800 text-orange-200" />}
                              {v.satellite_confirmed && <Badge text="SAT" color="bg-green-800 text-green-200" />}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-400">
                            <span>MMSI: <span className="text-slate-300 font-mono">{v.mmsi || '—'}</span></span>
                            <span>Type: <span className="text-slate-300">{v.type || '—'}</span></span>
                            <span>Speed: <span className="text-slate-300">{v.speed?.toFixed(1) || '0'} kt</span></span>
                            <span>Heading: <span className="text-slate-300">{v.course ? `${Math.round(v.course)}°` : '—'}</span></span>
                            <span>Status: <span className="text-slate-300">{v.navigational_status || '—'}</span></span>
                            <span>Dest: <span className="text-slate-300">{v.destination || '—'}</span></span>
                          </div>
                          {(v.latitude && v.longitude) && (
                            <div className="mt-1 text-cyan-500 font-mono text-[10px]">
                              {v.latitude.toFixed(5)}°N, {v.longitude.toFixed(5)}°E
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Air */}
            {activeSection === 'air' && (() => {
              const AIRCRAFT_CLASSES = ['aircraft', 'airplane', 'plane', 'helicopter', 'jet', 'fighter'];
              const satAircraft = (brief.satellite?.detections || [])
                .filter((d: any) => AIRCRAFT_CLASSES.some(c => (d.class_name || '').toLowerCase().includes(c)));
              const adsbMatched = satAircraft.filter((d: any) => d.adsb_icao24);
              const transponderOff = satAircraft.filter((d: any) => d.transponder_off);
              return (
              <div className="space-y-3">
                {/* Correlation summary banner */}
                {satAircraft.length > 0 && (
                  <div className="p-2.5 bg-slate-800/60 rounded-lg border border-slate-600/50 text-xs flex gap-4">
                    <span className="text-slate-300"><span className="text-green-400 font-bold">{adsbMatched.length}</span> / {satAircraft.length} satellite aircraft matched to ADS-B</span>
                    {transponderOff.length > 0 && (
                      <span className="text-orange-300 font-medium">{transponderOff.length} transponder-off</span>
                    )}
                  </div>
                )}

                {/* ADS-B live flights */}
                {brief.air.flights.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Live ADS-B ({brief.air.flights.length})</p>
                    {brief.air.flights.map((f: any, i: number) => (
                      <div key={i} className={`p-2.5 rounded-lg border text-xs ${
                        f.satellite_confirmed ? 'border-green-700/60 bg-green-900/10'
                        : f.isr_candidate || f.loitering_orbit ? 'border-purple-700/50 bg-purple-900/10'
                        : 'border-slate-700/50 bg-slate-800/40'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-slate-200 font-mono">{f.callsign || f.icao24 || '?'}</span>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {f.satellite_confirmed && <Badge text="SAT-CONFIRMED" color="bg-green-800 text-green-200" />}
                            {f.isr_candidate && <Badge text="ISR" color="bg-purple-800 text-purple-200" />}
                            {f.loitering_orbit && <Badge text="LOW-SLOW" color="bg-orange-800 text-orange-200" />}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-400">
                          <span>ICAO24: <span className="text-slate-300 font-mono">{f.icao24}</span></span>
                          <span>Country: <span className="text-slate-300">{f.origin_country || '—'}</span></span>
                          <span>Alt: <span className="text-slate-300">{f.altitude_ft?.toLocaleString() || '—'} ft</span></span>
                          <span>Speed: <span className="text-slate-300">{f.speed_kt || '—'} kt</span></span>
                          <span>On ground: <span className="text-slate-300">{f.on_ground ? 'Yes' : 'No'}</span></span>
                          <span>Squawk: <span className="text-slate-300 font-mono">{f.squawk || '—'}</span></span>
                        </div>
                        {f.satellite_confirmed && (
                          <div className="mt-1 text-green-400 text-[10px]">
                            Satellite position: {f.satellite_det_lat?.toFixed(5)}°N, {f.satellite_det_lon?.toFixed(5)}°E
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 text-center py-2">No live ADS-B aircraft detected over this AOI.</p>
                )}

                {/* Satellite-only aircraft (no ADS-B match) */}
                {transponderOff.length > 0 && (
                  <div className="p-3 bg-orange-900/20 rounded-lg border border-orange-700/40">
                    <p className="text-xs text-orange-300 font-medium mb-1">Transponder-off aircraft ({transponderOff.length})</p>
                    <p className="text-[10px] text-slate-500 mb-2">Visible in satellite imagery — no ADS-B signal within 2km. May be parked without active transponder or deliberately non-broadcasting.</p>
                    <div className="flex flex-wrap gap-1.5">
                      {transponderOff.slice(0, 20).map((d: any, i: number) => (
                        <div key={i} className="text-center">
                          {d.crop_b64 && <img src={`data:image/jpeg;base64,${d.crop_b64}`} alt=""
                            className="w-14 h-14 object-cover rounded border border-orange-800/60" style={{ imageRendering: 'pixelated' }} />}
                          <div className="text-[9px] text-slate-500 mt-0.5">{((d.confidence || 0) * 100).toFixed(0)}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Satellite aircraft that DID match ADS-B */}
                {adsbMatched.length > 0 && (
                  <div className="p-3 bg-green-900/10 rounded-lg border border-green-700/40">
                    <p className="text-xs text-green-300 font-medium mb-2">ADS-B identified from satellite ({adsbMatched.length})</p>
                    <div className="space-y-1.5">
                      {adsbMatched.map((d: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          {d.crop_b64 && <img src={`data:image/jpeg;base64,${d.crop_b64}`} alt=""
                            className="w-10 h-10 object-cover rounded border border-green-800/60 shrink-0" style={{ imageRendering: 'pixelated' }} />}
                          <div className="text-slate-300 space-y-0.5 min-w-0">
                            <div className="font-mono font-bold text-green-300">{d.adsb_callsign || d.adsb_icao24}</div>
                            <div className="text-[10px] text-slate-500">ICAO: {d.adsb_icao24} | {d.adsb_on_ground ? 'On ground' : `${d.adsb_altitude_ft}ft`} | {d.adsb_dist_m}m from sat pos</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              );
            })()}

            {/* Satellite */}
            {activeSection === 'satellite' && (
              <div className="space-y-3">
                {brief.satellite?.error ? (
                  <div className="text-xs text-red-400 p-3 bg-red-900/20 rounded">{brief.satellite.error}</div>
                ) : brief.satellite?.scene ? (
                  <>
                    <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50 text-xs space-y-1">
                      <div className="text-slate-300 font-medium">{brief.satellite.scene.title}</div>
                      <div className="grid grid-cols-2 gap-1 text-slate-400">
                        <span>Date: <span className="text-slate-200">{fmtDate(brief.satellite.scene.date || '')}</span></span>
                        <span>Cloud: <span className="text-slate-200">{brief.satellite.scene.cloud_coverage_pct?.toFixed(1)}%</span></span>
                        <span>Objects: <span className="text-green-400 font-bold">{brief.satellite.detection_count}</span></span>
                        <span>Tiles: <span className="text-slate-200">{brief.satellite.tiles_analyzed}</span></span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(brief.satellite.class_summary || {}).map(([cls, cnt]) => (
                          <span key={cls} className="px-1.5 py-0.5 bg-green-900/40 border border-green-700/50 rounded text-[10px] text-green-300">
                            {cls} × {cnt as number}
                          </span>
                        ))}
                      </div>
                    </div>
                    {brief.satellite.detections?.length > 0 && (
                      <div>
                        <p className="text-xs text-slate-400 mb-2">Detection crops (max 30 shown)</p>
                        <div className="flex flex-wrap gap-1.5 max-h-64 overflow-y-auto">
                          {brief.satellite.detections.slice(0, 30).map((d: any, i: number) => (
                            d.crop_b64 && (
                              <div key={i} className="relative">
                                <img src={`data:image/jpeg;base64,${d.crop_b64}`} alt={d.class_name}
                                  className="w-16 h-16 object-cover rounded border border-slate-600" />
                                <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] bg-black/80 text-white rounded-b px-0.5">
                                  {d.class_name} {((d.confidence || 0) * 100).toFixed(0)}%
                                </span>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-slate-500 text-center py-6">Satellite scan was not included. Enable it in the control bar.</p>
                )}
              </div>
            )}

            {/* History */}
            {activeSection === 'history' && (
              <div className="space-y-2">
                {brief.entity_history.count === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-6">No previously tracked entities found in this area (last 30 days).</p>
                ) : (
                  <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
                    {brief.entity_history.entities.map((e: any, i: number) => (
                      <div key={i} className="p-2.5 rounded-lg border border-slate-700/50 bg-slate-800/40 text-xs">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-slate-200">{e.name || 'Unknown Entity'}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-700 rounded text-slate-400">{e.entity_type}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-400">
                          <span>Observations: <span className="text-slate-300 font-bold">{e.observation_count}</span></span>
                          <span>Confidence: <span className="text-slate-300">{((e.confidence || 0) * 100).toFixed(0)}%</span></span>
                          <span>First seen: <span className="text-slate-300">{fmtDate(e.first_seen || '')}</span></span>
                          <span>Last seen: <span className="text-slate-300">{fmtDate(e.last_seen || '')}</span></span>
                        </div>
                        {e.contributing_modules?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {e.contributing_modules.map((m: string) => (
                              <span key={m} className="text-[9px] px-1.5 py-0.5 bg-blue-900/40 border border-blue-700/50 rounded text-blue-300">{m}</span>
                            ))}
                          </div>
                        )}
                        {e.distance_km && (
                          <div className="mt-0.5 text-slate-500 text-[10px]">{e.distance_km.toFixed(1)} km from AOI center</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Scenario Intelligence */}
            {activeSection === 'scenario' && (() => {
              const sc = brief.scenario || {}
              const groundCtx: any[] = sc.ground_context || []
              const gdeltEvents: any[] = sc.civil_unrest_events || []
              const moves = sc.vessel_movements || {}
              const departed: any[] = moves.departed || []
              const arrived: any[] = moves.arrived || []
              const riskIndex: number = sc.protest_risk_index || 0

              const riskLabel = riskIndex >= 5 ? 'CRITICAL' : riskIndex >= 4 ? 'HIGH' : riskIndex >= 3 ? 'MEDIUM' : riskIndex >= 2 ? 'LOW' : 'MINIMAL'
              const riskColor = riskIndex >= 5 ? 'text-red-400' : riskIndex >= 4 ? 'text-orange-400' : riskIndex >= 3 ? 'text-yellow-400' : 'text-green-400'
              const riskBarColor = riskIndex >= 5 ? 'bg-red-500' : riskIndex >= 4 ? 'bg-orange-500' : riskIndex >= 3 ? 'bg-yellow-500' : 'bg-green-500'

              const riskBadgeCls = (score: number) =>
                score >= 5 ? 'bg-red-900/60 text-red-300 border border-red-700/50' :
                score >= 4 ? 'bg-orange-900/60 text-orange-300 border border-orange-700/50' :
                score >= 3 ? 'bg-yellow-900/60 text-yellow-300 border border-yellow-700/50' :
                'bg-slate-700/60 text-slate-400 border border-slate-600/50'

              const iconForType = (icon: string) => {
                if (icon === 'government') return <Shield className="w-3 h-3" />
                if (icon === 'crowd') return <Users className="w-3 h-3" />
                if (icon === 'transport') return <Plane className="w-3 h-3" />
                return <MapPin className="w-3 h-3" />
              }

              return (
                <div className="space-y-4">
                  {/* Protest Risk Meter */}
                  <div className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="flex items-center gap-1.5 text-slate-300 font-medium">
                        <TrendingUp className="w-3.5 h-3.5" />Protest Risk Index
                      </span>
                      <span className={`font-bold text-[11px] uppercase tracking-wide ${riskColor}`}>{riskLabel}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full transition-all ${riskBarColor}`} style={{ width: `${(riskIndex / 5) * 100}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Based on high-risk POI proximity — {sc.high_risk_pois?.length || 0} sensitive site(s) found</div>
                  </div>

                  {/* Ground Context (OSM POIs) */}
                  {groundCtx.length > 0 ? (
                    <div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2">Ground Context — {groundCtx.length} sites</div>
                      <div className="space-y-1">
                        {groundCtx.map((poi: any, i: number) => (
                          <div key={i} className="flex items-start gap-2 p-2 bg-slate-800/40 rounded border border-slate-700/30 text-xs">
                            <span className={`flex items-center justify-center w-5 h-5 rounded text-[9px] font-bold shrink-0 mt-0.5 ${riskBadgeCls(poi.risk_score)}`}>
                              {poi.risk_score}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="text-slate-400">{iconForType(poi.icon_type)}</span>
                                <span className="text-slate-200 font-medium truncate">{poi.name}</span>
                              </div>
                              <div className="text-slate-500 text-[10px]">{poi.category} · {poi.dist_km} km away</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 text-center py-3 bg-slate-800/20 rounded border border-slate-700/30">
                      No crowd-relevant POIs found via OpenStreetMap within the AOI radius
                    </div>
                  )}

                  {/* Civil Unrest Events (GDELT) */}
                  <div>
                    <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2">Civil Unrest Events — GDELT (last 7 days)</div>
                    {gdeltEvents.length > 0 ? (
                      <div className="space-y-1">
                        {gdeltEvents.map((ev: any, i: number) => (
                          <a key={i} href={ev.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-start gap-2 p-2 bg-slate-800/40 rounded border border-slate-700/30 hover:border-orange-700/50 transition-colors group text-xs">
                            <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-orange-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <div className="text-slate-300 line-clamp-2 leading-snug">{ev.title || 'Untitled'}</div>
                              <div className="text-slate-500 text-[10px] mt-0.5">{ev.domain} · {ev.date || '—'}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500 text-center py-3 bg-slate-800/20 rounded border border-slate-700/30">
                        No protest/unrest articles found in the last 7 days
                      </div>
                    )}
                  </div>

                  {/* Vessel Movements */}
                  {(departed.length > 0 || arrived.length > 0) && (
                    <div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2">
                        Vessel Movements{moves.snapshot_age_hours != null ? ` — vs scan ${moves.snapshot_age_hours}h ago` : ''}
                      </div>
                      <div className="space-y-1">
                        {departed.map((v: any, i: number) => (
                          <div key={`d${i}`} className="flex items-start gap-2 p-2 bg-red-900/20 border border-red-800/40 rounded text-xs">
                            <Anchor className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                            <div>
                              <span className="text-red-300 font-semibold">DEPARTED: {v.name}</span>
                              <div className="text-slate-500 text-[10px]">MMSI {v.mmsi}{v.ship_type ? ` · ${v.ship_type}` : ''}{v.last_seen ? ` · last seen ${v.last_seen.slice(0, 16).replace('T', ' ')}` : ''}</div>
                            </div>
                          </div>
                        ))}
                        {arrived.map((v: any, i: number) => (
                          <div key={`a${i}`} className="flex items-start gap-2 p-2 bg-green-900/20 border border-green-800/40 rounded text-xs">
                            <Anchor className="w-3 h-3 text-green-400 mt-0.5 shrink-0" />
                            <div>
                              <span className="text-green-300 font-semibold">ARRIVED: {v.name}</span>
                              <div className="text-slate-500 text-[10px]">MMSI {v.mmsi}{v.ship_type ? ` · ${v.ship_type}` : ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {departed.length === 0 && arrived.length === 0 && moves.snapshot_age_hours != null && (
                    <div className="text-xs text-slate-500 text-center py-2 bg-slate-800/20 rounded border border-slate-700/30">
                      No vessel movements since last scan {moves.snapshot_age_hours}h ago ({moves.previous_count || 0} → {moves.current_count || 0} vessels)
                    </div>
                  )}
                </div>
              )
            })()}

            {/* AI Report */}
            {activeSection === 'report' && (
              <div className="space-y-3">
                {!report && !generating && (
                  <div className="text-center py-8">
                    <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                    <p className="text-sm text-slate-400 mb-4">Generate a formal GEOINT intelligence assessment from the collected data.</p>
                    <Button onClick={generateReport} className="bg-purple-700 hover:bg-purple-600 text-white">
                      Generate AI Report
                    </Button>
                  </div>
                )}
                {generating && (
                  <div className="flex items-center justify-center gap-3 py-12 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                    <span className="text-sm">Grok-3 is analyzing the intelligence picture...</span>
                  </div>
                )}
                {report && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-400" />
                        <span className="text-sm font-semibold text-slate-200">Intelligence Assessment</span>
                        <Badge text="GROK-3" color="bg-purple-800/60 text-purple-300" />
                      </div>
                      <button onClick={generateReport} disabled={generating}
                        className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                        Regenerate
                      </button>
                    </div>
                    <div className="prose prose-invert prose-sm max-w-none p-4 bg-slate-800/40 rounded-lg border border-slate-700/50 text-xs leading-relaxed text-slate-300 whitespace-pre-wrap font-mono">
                      {report}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </>
      )}
    </div>
  )
}
